<template>
  <default-layout>
    <div class="fluid-panel">
      <!-- LEFT RAIL: both fleets, stacked -->
      <v-scrollbar class="panel-aside">
        <div
          v-for="s in sides"
          :key="`edit-${s.name}`">
          <div class="panel-aside-info">
            <h2>{{ $t('page.fight_simulator.player', {number: s.number}) }}</h2>
            <p>{{ $t(`page.fight_simulator.${s.name}`) }}</p>
          </div>

          <div class="panel-aside-bloc simulator-fleet-actions">
            <button
              class="default-button is-small"
              @click="clearAll(s.name)">
              {{ $t('page.fight_simulator.clear_all') }}
            </button>
            <button
              class="default-button is-small"
              v-tooltip.bottom="$t('page.fight_simulator.export_hint')"
              @click="exportFleet(s)">
              {{ $t('page.fight_simulator.export') }}
            </button>
          </div>

          <div class="panel-aside-bloc simulator-fleet-stats army-header">
            <div>
              <div
                class="def-list-prop"
                v-tooltip.bottom="$t('galaxy.selection.view.army_repair')">
                <span class="sr-only">{{ $t('galaxy.selection.view.army_repair') }}:</span>
                {{ statsFor(s).repair | integer }}
                <svgicon name="ship/repair" aria-hidden="true" />
              </div>
              <div
                class="def-list-prop"
                v-tooltip.bottom="$t('galaxy.selection.view.army_raid')">
                <span class="sr-only">{{ $t('galaxy.selection.view.army_raid') }}:</span>
                {{ statsFor(s).raid | integer }}
                <svgicon name="ship/raid" aria-hidden="true" />
              </div>
              <div
                class="def-list-prop"
                v-tooltip.bottom="$t('galaxy.selection.view.army_invasion')">
                <span class="sr-only">{{ $t('galaxy.selection.view.army_invasion') }}:</span>
                {{ statsFor(s).invasion | integer }}
                <svgicon name="ship/invasion" aria-hidden="true" />
              </div>
            </div>
            <div>
              <div
                class="def-list-prop"
                v-tooltip.bottom="$t('galaxy.selection.view.army_maintenance')">
                <span class="sr-only">{{ $t('galaxy.selection.view.army_maintenance') }}:</span>
                {{ statsFor(s).maintenance | income(0) }}
                <svgicon name="resource/credit" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div class="panel-aside-bloc simulator-army-bloc">
            <simulator-army
              mode="edit"
              :theme="s.theme"
              :tiles="s.data.tiles"
              :activeIdx="activeIdxFor(s.name)"
              @pick-tile="onPickTile(s.name, $event)"
              @clear-tile="onClearTile(s.name, $event)"
              @bump-up="onBump(s.name, $event, 'next')"
              @bump-down="onBump(s.name, $event, 'prev')"
              @hover="(key, level) => onHoverShip(key, level, s.name)" />
          </div>

          <hr class="margin">
        </div>
      </v-scrollbar>

      <div class="panel-content is-full-sized">
        <div class="panel-header">
          <h1>
            <strong>{{ $t('page.fight_simulator.title') }}</strong>
          </h1>

          <div class="simulator-balance default-input">
            <label for="balance_preset">{{ $t('page.fight_simulator.balance') }}</label>
            <select id="balance_preset" v-model="balance">
              <option value="baseline">{{ $t('page.fight_simulator.balance_baseline') }}</option>
              <option value="hard_counter_rps">{{ $t('page.fight_simulator.balance_hard_counter_rps') }}</option>
            </select>
          </div>

          <div class="simulator-launch">
            <button
              @click="fight(1)"
              class="default-button">
              {{ $t('page.fight_simulator.launch') }}
            </button>
            <button
              @click="fight(100)"
              class="default-button">
              {{ $t('page.fight_simulator.launch_many') }}
            </button>
          </div>
        </div>

        <v-scrollbar class="content">
          <simulator-ship-picker
            v-if="activePicker && !isMobileView"
            ref="picker"
            :theme="activePicker.side === 'attacker' ? attackerTheme : defenderTheme"
            :level="placementLevel"
            :stack-by-class="stackByClass"
            @update:level="placementLevel = $event"
            @update:stack="onUpdateStack"
            @pick="onPickShip"
            @hover="onHoverShip($event, placementLevel, activePicker.side)" />

          <div
            v-else-if="logs"
            class="simulator-results">
            <div
              v-if="aggregate"
              class="simulator-runs-summary">
              <div class="runs-counts">
                <span :style="{ color: sideColor(attackerTheme) }">
                  {{ $t('page.fight_simulator.runs_attacker_wins', { count: aggregate.attacker_wins }) }}
                </span>
                <span
                  v-if="aggregate.draws > 0"
                  class="runs-draws">
                  {{ $t('page.fight_simulator.runs_draws', { count: aggregate.draws }) }}
                </span>
                <span :style="{ color: sideColor(defenderTheme) }">
                  {{ $t('page.fight_simulator.runs_defender_wins', { count: aggregate.defender_wins }) }}
                </span>
              </div>
              <div class="runs-bar">
                <div :style="{ flexGrow: aggregate.attacker_wins, background: sideColor(attackerTheme) }"></div>
                <div :style="{ flexGrow: aggregate.draws, background: '#4a5261' }"></div>
                <div :style="{ flexGrow: aggregate.defender_wins, background: sideColor(defenderTheme) }"></div>
              </div>
              <div class="runs-sub">{{ $t('page.fight_simulator.runs_of_n', { n: aggregate.n }) }}</div>
              <div class="runs-losses">
                <div>{{ lossesLine('attacker', 'attackers') }}</div>
                <div>{{ lossesLine('defender', 'defenders') }}</div>
              </div>
            </div>

            <div class="simulator-tabs">
              <button
                class="simulator-tab"
                :class="{ 'is-active': resultTab === 'log' }"
                :aria-pressed="resultTab === 'log' ? 'true' : 'false'"
                @click="resultTab = 'log'">
                {{ $t('page.fight_simulator.tab_log') }}
              </button>
              <button
                class="simulator-tab"
                :class="{ 'is-active': resultTab === 'debug' }"
                :aria-pressed="resultTab === 'debug' ? 'true' : 'false'"
                @click="resultTab = 'debug'">
                {{ $t('page.fight_simulator.tab_debug') }}
              </button>
            </div>

            <div
              v-if="aggregate && resultTab !== 'log'"
              class="simulator-sample-note">
              {{ $t('page.fight_simulator.sample_label', { n: aggregate.n }) }}
            </div>

            <template v-if="resultTab === 'log'">
              <div
                v-for="side in ['attackers', 'defenders']"
                :key="`results-${side}`"
                class="simulator-results-side">
                <h2>
                  {{ $t(`page.fight_simulator.results_${side}`) }}
                </h2>
                <div
                  v-for="character in initialCharacters[side]"
                  :key="`char-${side}-${character.id}`"
                  class="simulator-results-army">
                  <simulator-army
                    mode="display"
                    :theme="theme(character.owner.faction)"
                    :tiles="displayTilesFor(character)"
                    :diff="aggregate ? null : displayTilesFor(finalCharacterFor(side, character.id))"
                    :agg="aggregate ? aggTilesFor(side, character) : null"
                    :agg-total="aggregate ? aggregate.n : 0" />
                </div>
              </div>

              <div class="fight-report">
                <div class="title">
                  {{ $t('panel.operations.fight_course') }}
                </div>
                <div
                  v-if="aggregate"
                  class="simulator-sample-note">
                  {{ $t('page.fight_simulator.sample_label', { n: aggregate.n }) }}
                </div>
                <div
                  class="round"
                  v-for="(round, j) in logs"
                  :key="`round-${j}`">
                  <div class="round-title">
                    {{ j + 1 }}
                  </div>
                  <div class="round-content">
                    <simulator-round-log
                      :round="round"
                      :get-ship="getShip"
                      :compute-strikes="computeStrikes" />
                  </div>
                </div>
              </div>
            </template>

            <simulator-debug-view
              v-else
              :round-states="roundStates"
              :logs="logs"
              :get-ship="getShip"
              :compute-strikes="computeStrikes" />

            <hr class="margin">
          </div>

          <div
            v-else
            class="simulator-empty-hint">
            {{ $t('page.fight_simulator.empty_hint') }}
          </div>
        </v-scrollbar>
      </div>

      <!-- RIGHT RAIL: hotkeys + ship info card -->
      <v-scrollbar class="panel-aside">
        <div class="panel-aside-info">
          <h2>{{ $t('page.fight_simulator.controls') }}</h2>
        </div>

        <div class="panel-aside-bloc simulator-hotkeys">
          <ul>
            <li><kbd>Shift</kbd> + {{ $t('page.fight_simulator.hotkey_fill') }}</li>
            <li><kbd>Ctrl</kbd>+<kbd>Shift</kbd> + {{ $t('page.fight_simulator.hotkey_override') }}</li>
          </ul>
        </div>

        <hr class="margin">

        <div
          ref="infoCard"
          class="panel-aside-bloc simulator-info-card">
          <template v-if="infoShip">
            <!-- the in-game card is a fixed 300px wide: shrink it to fit
                 narrower asides instead of letting it overflow -->
            <div
              class="simulator-ship-card"
              :style="{ zoom: cardZoom }">
              <!-- show-cost: in game only blueprints (and previewed
                   formations) show a cost, but every simulator ship is a
                   blueprint -->
              <ship-card
                standalone
                show-cost
                :shipKey="infoShip.key"
                :ship="infoLiveShip"
                :theme="infoTheme"
                :tuneShip="tuneShip" />
            </div>
            <template v-if="balanceChanges.length">
              <h3>{{ $t('page.fight_simulator.balance_changes') }}</h3>
              <table class="info-stats">
                <tr
                  v-for="st in balanceChanges"
                  :key="st.label">
                  <td>{{ st.label }}</td>
                  <td>
                    <span class="base-val">{{ st.base }}</span>
                    <span class="arrow">→</span>
                    <span class="tuned-val">{{ st.tuned }}</span>
                  </td>
                </tr>
              </table>
            </template>
          </template>
          <p
            v-else
            class="info-hint">
            {{ $t('page.fight_simulator.info_hint') }}
          </p>
        </div>
      </v-scrollbar>
    </div>

    <!-- Phone: the picker is a bottom sheet, not the next panel down.
         Stacked, the content panel sits below BOTH fleets, so choosing a
         ship meant scrolling past the other player's fleet and losing
         sight of the slot being filled. The sheet keeps the slot on
         screen (the tile is scrolled into view and stays highlighted)
         and names it in the header. -->
    <div
      v-if="isMobileView && activePicker"
      class="sim-sheet-root">
      <div
        class="sim-sheet-backdrop"
        @click="activePicker = null" />

      <div
        class="sim-sheet"
        role="dialog"
        aria-modal="true"
        :aria-label="$t('page.fight_simulator.picker_title')"
        @keydown.esc="activePicker = null">
        <div class="sim-sheet-head">
          <span
            class="sim-sheet-side"
            :class="`f-${activePicker.side === 'attacker' ? attackerTheme : defenderTheme}`">
            {{ $t('page.fight_simulator.player', { number: activePicker.side === 'attacker' ? 1 : 2 }) }}
          </span>
          <span class="sim-sheet-slot">{{ activeSlotLabel }}</span>
          <button
            class="sim-sheet-close"
            :aria-label="$t('a11y_portal.close')"
            @click="activePicker = null">
            <svgicon name="close" aria-hidden="true" />
          </button>
        </div>

        <div class="sim-sheet-body">
          <simulator-ship-picker
            ref="picker"
            :theme="activePicker.side === 'attacker' ? attackerTheme : defenderTheme"
            :level="placementLevel"
            :stack-by-class="stackByClass"
            @update:level="placementLevel = $event"
            @update:stack="onUpdateStack"
            @pick="onPickShip"
            @hover="onHoverShip($event, placementLevel, activePicker.side)" />
        </div>
      </div>
    </div>
  </default-layout>
</template>

<script>
import viewport from '@/utils/viewport';
import DefaultLayout from '@/portal/layouts/Default.vue';
import SimulatorArmy from '@/portal/components/SimulatorArmy.vue';
import SimulatorShipPicker from '@/portal/components/SimulatorShipPicker.vue';
import SimulatorRoundLog from '@/portal/components/SimulatorRoundLog.vue';
import SimulatorDebugView from '@/portal/components/SimulatorDebugView.vue';
import ShipCard from '@/game/components/card/ShipCard.vue';
import renderFleetBlob, { fleetStats } from '@/portal/fleet-image';
import { copyPngBlob } from '@/utils/image-clipboard';

const TILE_COUNT = 18;
const SHIP_CARD_WIDTH = 300;
const LINE_SIZE = 3;

export default {
  name: 'fight-simulator',
  data() {
    return {
      // tiles: Array<null | { ship_key, level }>. level is 0-indexed (combat
      // reads it directly; the army tile shows level + 1).
      attacker: { tiles: Array(TILE_COUNT).fill(null) },
      defender: { tiles: Array(TILE_COUNT).fill(null) },
      placementLevel: 0, // level newly-placed ships get (0-indexed)
      stackByClass: {}, // class -> chosen stack size; held here so it survives the picker closing
      balance: 'baseline', // 'baseline' (live data) | a Sim.Balance preset
      balancePresets: {}, // { presetName: { baseShipKey: { field: value } } }, from the API
      activePicker: null, // { side: 'attacker'|'defender', idx: number }
      // last hovered/clicked ship, shown in the right rail:
      // { key, level (0-indexed), side: 'attacker'|'defender' }
      infoRef: null,
      cardZoom: 1,
      resultTab: 'log', // 'log' | 'debug' (results view tab)
      logs: null,
      initialCharacters: { attackers: [], defenders: [] },
      finalCharacters: { attackers: [], defenders: [] },
      // Multi-run mode: the response's `runs` aggregate (null after a single
      // run). When set, the army diff overlay is replaced by the per-tile
      // survival distribution, and logs/debug show run 0 as an example battle.
      aggregate: null,
    };
  },
  async created() {
    try {
      const { data } = await this.$axios.get('/fight-balances');
      this.balancePresets = data;
    } catch (e) {
      // Non-fatal: the info card just falls back to base stats with no deltas.
    }
  },
  mounted() {
    this.updateCardZoom();
    if (window.ResizeObserver) {
      this.cardResizeObserver = new window.ResizeObserver(() => this.updateCardZoom());
      this.cardResizeObserver.observe(this.$refs.infoCard);
    }
  },
  beforeDestroy() {
    if (this.cardResizeObserver) this.cardResizeObserver.disconnect();
  },
  computed: {
    data() { return this.$store.state.portal.data; },
    shipsData() { return this.$store.state.portal.data.ship || []; },
    attackerTheme() { return this.themeOf('myrmezir'); },
    defenderTheme() { return this.themeOf('tetrarchy'); },
    infoShip() {
      if (!this.infoRef) return null;
      return this.shipsData.find((s) => s.key === this.infoRef.key) || null;
    },
    infoTheme() {
      return this.infoRef && this.infoRef.side === 'defender' ? this.defenderTheme : this.attackerTheme;
    },
    // The card only applies ship XP (level morale, +dmg, +handling/shield/
    // flak) to a live ship, so a levelled simulator ship is handed over as
    // a fresh full-hull instance. Level 0 needs none: blueprint stats match.
    infoLiveShip() {
      if (!this.infoShip || !this.infoRef.level) return undefined;
      const { unit_hull: hull, unit_count: count } = this.tuneShip(this.infoShip);
      return {
        name: '',
        level: this.infoRef.level,
        experience: 0,
        units: Array.from({ length: count }, () => ({ hull })),
      };
    },
    // Drives the stacked left-rail fleets. Avoids `this[side]` in the template:
    // inside a v-for render callback `this` isn't the component, so indexing it
    // throws (black screen on mount).
    isMobileView() { return viewport.isMobile; },
    // "L2 · 3" — which battle line, and which of its three tiles. The
    // sheet covers the grid, so the header has to say what is being
    // filled.
    activeSlotLabel() {
      if (!this.activePicker) return '';
      const { idx } = this.activePicker;
      return this.$t('page.fight_simulator.slot_label', {
        line: Math.floor(idx / LINE_SIZE) + 1,
        slot: (idx % LINE_SIZE) + 1,
      });
    },
    sides() {
      return [
        { name: 'attacker', number: 1, data: this.attacker, theme: this.attackerTheme },
        { name: 'defender', number: 2, data: this.defender, theme: this.defenderTheme },
      ];
    },
    // Stats the selected balance preset changes for the hovered ship (the card
    // above already shows the tuned values; this lists what they were).
    balanceChanges() {
      if (!this.infoShip) return [];
      const s = this.infoShip;
      const ov = this.overridesFor(s.key) || {};
      const t = (key) => this.$t(`page.fight_simulator.${key}`);
      const num = (field, label) => {
        const base = field === 'unit_armor' ? (s[field] || 0) : s[field];
        const tuned = ov[field] !== undefined ? ov[field] : base;
        return { label, base, tuned, changed: tuned !== base };
      };
      const str = (field, label) => {
        const base = this.strikes(s[field]);
        const tuned = ov[field] !== undefined ? this.strikes(ov[field]) : base;
        return { label, base, tuned, changed: tuned !== base };
      };
      return [
        num('unit_handling', t('stat_handling')),
        num('unit_hull', t('stat_hull')),
        num('unit_shield', t('stat_shield')),
        num('unit_interception', t('stat_flak')),
        num('unit_armor', t('stat_armor')),
        str('unit_energy_strikes', t('stat_energy')),
        str('unit_explosive_strikes', t('stat_explosive')),
        num('unit_raid_coef', t('stat_raid')),
      ].filter((st) => st.changed);
    },
    // Per-round battle snapshots for the Debug View, replayed once from the
    // action log + initial state. This is a computed, so Vue caches it until a
    // new fight changes `logs` — round navigation never re-runs the replay, and
    // it needs no extra API calls (everything comes from the one /run-fight).
    roundStates() {
      const rounds = this.logs || [];
      const initial = this.initialCharacters;
      const chars = (initial.attackers || []).concat(initial.defenders || []);

      const charSide = {};
      (initial.attackers || []).forEach((c) => { charSide[c.id] = 'attackers'; });
      (initial.defenders || []).forEach((c) => { charSide[c.id] = 'defenders'; });

      // Mutable per-tile state + a stable display order per side.
      const state = {};
      const order = { attackers: [], defenders: [] };
      chars.forEach((c) => {
        const side = charSide[c.id];
        (c.army.tiles || []).forEach((tile) => {
          if (tile.ship_status === 'filled' && tile.ship) {
            const maxHp = (tile.ship.units || []).reduce((a, u) => a + u.hull, 0);
            const key = `${c.id}:${tile.id}`;
            state[key] = {
              key,
              ship_key: tile.ship.key,
              level: tile.ship.level,
              maxHp,
              hp: maxHp,
              onField: false,
              escaped: false, // morale broke -> routing/routed
              destroyed: false,
              withdrawn: false, // survived to the end and stood down to the army
            };
            if (order[side]) order[side].push(key);
          }
        });
      });

      const refKey = (ref) => `${ref.character}:${ref.tile}`;
      const snapshots = [];

      rounds.forEach((actions, ri) => {
        const dmg = { attackers: {}, defenders: {} };

        (actions || []).forEach((action) => {
          if (action.type === 'transfer') {
            const st = state[refKey(action.source)];
            if (st) {
              // 'field' = deployed in; 'army' = pulled out (mid-battle this only
              // happens to a routed ship the turn after it breaks).
              st.onField = action.data.target === 'field';
            }
          } else if (action.type === 'destroyed') {
            const st = state[refKey(action.source)];
            if (st) { st.destroyed = true; st.hp = 0; st.onField = false; }
          } else if (action.type === 'escaping') {
            // Morale broke: the ship is routing. It STAYS on the field this turn
            // (still targetable — that's why it keeps taking damage) and is pulled
            // to the army by next turn's cleaning (a transfer -> army event).
            const st = state[refKey(action.source)];
            if (st) st.escaped = true;
          } else if (action.type === 'attack') {
            const dealt = (action.data.actions || []).reduce(
              (sum, a) => sum + (a.strikes || []).reduce((s, k) => s + (k.damages || 0), 0),
              0,
            );
            const tgt = state[refKey(action.data.target)];
            if (tgt) tgt.hp = Math.max(0, tgt.hp - dealt);
            const sideOfSrc = charSide[action.source.character];
            const src = state[refKey(action.source)];
            if (src && sideOfSrc) {
              dmg[sideOfSrc][src.ship_key] = (dmg[sideOfSrc][src.ship_key] || 0) + dealt;
            }
          }
        });

        const snapSide = (side) => order[side].map((k) => ({ ...state[k] }));
        const dmgSide = (side) => Object.keys(dmg[side])
          .map((shipKey) => ({ ship_key: shipKey, total: Math.round(dmg[side][shipKey]) }))
          .sort((a, b) => b.total - a.total);

        snapshots.push({
          round: ri + 1,
          attackers: snapSide('attackers'),
          defenders: snapSide('defenders'),
          damage: { attackers: dmgSide('attackers'), defenders: dmgSide('defenders') },
        });
      });

      // When the battle resolves (or hits the turn cap) the engine returns the
      // alive on-field survivors to the army — a withdrawal that happens AFTER
      // the last per-round log flush, so it never appears in `logs`. Synthesize
      // it on the final snapshot so survivors read "Withdrawn" instead of being
      // frozen mid-fight as "On field".
      const last = snapshots[snapshots.length - 1];
      if (last) {
        ['attackers', 'defenders'].forEach((side) => {
          last[side].forEach((t) => {
            if (t.onField && !t.destroyed) {
              t.onField = false;
              // Non-routed survivors stood down; routed ones already read as
              // withdrawn once off-field (see SimulatorDebugView.statusOf).
              if (!t.escaped) t.withdrawn = true;
            }
          });
        });
      }

      return snapshots;
    },
  },
  watch: {
    // Phones only: the sheet covers the bottom ~55% of the screen, so
    // park the highlighted tile in the upper third of what is left —
    // scrollIntoView('center') would drop it right behind the sheet's
    // top edge.
    activePicker(value) {
      if (!value || !this.isMobileView) return;
      this.$nextTick(() => {
        const tile = this.$el && this.$el.querySelector('.simulator-army .tile.is-active');
        const scroller = tile && tile.closest('.layout-content');
        if (!tile || !scroller) return;

        const tileTop = tile.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
        const target = scroller.scrollTop + tileTop - (scroller.clientHeight * 0.22);
        scroller.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
      });
    },
  },
  methods: {
    displayTilesFor(character) {
      if (!character || !character.army) return Array(TILE_COUNT).fill(null);
      return character.army.tiles.map((t) => (
        t.ship_status === 'filled' && t.ship
          ? { ship_key: t.ship.key, level: t.ship.level, units: t.ship.units }
          : null
      ));
    },
    finalCharacterFor(side, id) {
      return (this.finalCharacters[side] || []).find((c) => c.id === id);
    },
    activeIdxFor(side) {
      return this.activePicker && this.activePicker.side === side
        ? this.activePicker.idx
        : -1;
    },
    onPickTile(side, idx) {
      this.activePicker = { side, idx };
      this.$nextTick(() => {
        if (this.$refs.picker) this.$refs.picker.focus();
      });
    },
    onClearTile(side, idx) {
      this.$set(this[side].tiles, idx, null);
      if (this.activePicker
          && this.activePicker.side === side
          && this.activePicker.idx === idx) {
        this.activePicker = null;
      }
    },
    clearAll(side) {
      this[side].tiles = Array(TILE_COUNT).fill(null);
      if (this.activePicker && this.activePicker.side === side) {
        this.activePicker = null;
      }
    },
    onBump(side, idx, direction) {
      const current = this[side].tiles[idx];
      if (!current) return;
      const ship = this.shipsData.find((s) => s.key === current.ship_key);
      if (!ship) return;
      const variants = this.shipsData
        .filter((s) => s.model === ship.model)
        .sort((a, b) => a.unit_count - b.unit_count);
      const i = variants.findIndex((s) => s.key === current.ship_key);
      const next = direction === 'next' ? variants[i + 1] : variants[i - 1];
      if (next) this.$set(this[side].tiles, idx, { ship_key: next.key, level: current.level });
    },
    onPickShip(shipKey, mods = {}) {
      if (!this.activePicker) return;
      const { side, idx } = this.activePicker;
      this.infoRef = { key: shipKey, level: this.placementLevel, side };
      const tiles = this[side].tiles;
      const make = () => ({ ship_key: shipKey, level: this.placementLevel });

      // Shift: fill the active slot's column / L-group (LINE_SIZE tiles), empty
      // slots only — unless Ctrl is also held (override). Other columns untouched.
      if (mods.shift) {
        const start = Math.floor(idx / LINE_SIZE) * LINE_SIZE;
        for (let i = start; i < start + LINE_SIZE; i += 1) {
          if (mods.ctrl || tiles[i] === null) this.$set(tiles, i, make());
        }
      } else {
        this.$set(tiles, idx, make());
      }

      // In every case advance the selector to the next empty slot in the
      // SAME battle line, then on to the next line once it's full.
      this.advanceSelector(side, idx);
    },
    // Fleet-building order = deployment order: tiles fill down the current
    // battle line (L-group) before moving to the next one, matching how the
    // game fills an army and how lines commit to the field one per turn.
    // Tile indices are already line-major (0,1,2 = L1), so this is a plain
    // sequential scan with wrap-around.
    advanceSelector(side, fromIdx) {
      const tiles = this[side].tiles;
      for (let k = 1; k <= TILE_COUNT; k += 1) {
        const nextIdx = (fromIdx + k) % TILE_COUNT;
        if (tiles[nextIdx] === null) {
          this.activePicker = { side, idx: nextIdx };
          return;
        }
      }
      this.activePicker = null; // fleet full
    },
    // The selected balance preset's overrides for a ship key, matching the base
    // key or any of its stack variants (e.g. corvette_1 -> corvette_1v2).
    overridesFor(shipKey) {
      const preset = this.balancePresets[this.balance];
      if (!preset) return null;
      const baseKey = Object.keys(preset).find(
        (k) => shipKey === k || shipKey.startsWith(`${k}v`),
      );
      return baseKey ? preset[baseKey] : null;
    },
    // ShipCard hook: the ship's stats under the selected balance preset.
    tuneShip(ship) {
      const ov = ship && this.overridesFor(ship.key);
      return ov ? { ...ship, ...ov } : ship;
    },
    onHoverShip(shipKey, level, side) {
      if (shipKey) this.infoRef = { key: shipKey, level: level || 0, side };
    },
    updateCardZoom() {
      const bloc = this.$refs.infoCard;
      if (!bloc || !bloc.clientWidth) return;
      this.cardZoom = Math.min(1, bloc.clientWidth / SHIP_CARD_WIDTH);
    },
    // Ship data by key under the selected balance preset.
    tunedShipFor(key) {
      return this.tuneShip(this.shipsData.find((s) => s.key === key));
    },
    // Fleet totals for the header above each grid (same numbers the export
    // image prints).
    statsFor(side) {
      return fleetStats(side.data.tiles, this.tunedShipFor);
    },
    async exportFleet(side) {
      // A tuned preset changes the numbers on the picture, so say which one.
      const footer = this.balance === 'baseline'
        ? null
        : `${this.$t('page.fight_simulator.balance')}: ${this.$t(`page.fight_simulator.balance_${this.balance}`)}`;
      const blob = await renderFleetBlob(
        side.data.tiles,
        this.tunedShipFor,
        (key, params) => this.$t(key, params),
        { theme: side.theme, footer },
      );
      if (await copyPngBlob(blob)) {
        this.$toasted.success(this.$t('page.fight_simulator.export_copied'));
      } else {
        this.$toasted.error(this.$t('page.fight_simulator.export_failed'));
      }
    },
    onUpdateStack({ category, size }) {
      this.$set(this.stackByClass, category, size);
    },
    async fight(runs = 1) {
      try {
        const { data } = await this.$axios.post(
          '/run-fight',
          { attacker: this.attacker, defender: this.defender, balance: this.balance, runs },
        );
        const { initial, final, logs } = data;

        this.logs = logs;
        this.initialCharacters = initial;
        this.finalCharacters = final;
        this.aggregate = data.runs || null;
        this.activePicker = null;
      } catch (err) {
        this.$toastChangesetError(err);
      }
    },
    // Per-tile 100-run aggregates for one character, aligned to the army's
    // tile order (the shape SimulatorArmy's `agg` prop expects).
    aggTilesFor(side, character) {
      const entry = ((this.aggregate.sides || {})[side] || [])
        .find((e) => e.character === character.id);
      if (!entry) return null;
      const byId = {};
      entry.tiles.forEach((t) => { byId[t.id] = t; });
      return character.army.tiles.map((t) => (
        t.ship_status === 'filled' && t.ship ? (byId[t.id] || null) : null
      ));
    },
    // Faction accent per theme — same values SimulatorArmy hardcodes for the
    // fleet frames (the SCSS theme map isn't reachable from here either).
    sideColor(theme) {
      return { red: '#bc2433', 'dark-blue': '#3f66df' }[theme] || '#4a5261';
    },
    lossesLine(sideKey, sidePlural) {
      const l = (this.aggregate.losses || {})[sidePlural];
      if (!l) return '';
      const side = this.$t(`page.fight_simulator.${sideKey}`);
      if (!l.max) return this.$t('page.fight_simulator.runs_losses_none', { side });
      if (l.p10 === l.p90) {
        return this.$t('page.fight_simulator.runs_losses_single', { side, count: l.p90, max: l.max });
      }
      return this.$t('page.fight_simulator.runs_losses_range', {
        side, low: l.p10, high: l.p90, max: l.max,
      });
    },
    getShip(ref) {
      const characters = this.initialCharacters.attackers.concat(this.initialCharacters.defenders);
      const character = characters.find((c) => c.id === ref.character);
      const tile = character.army.tiles.find((t) => t.id === ref.tile);
      return {
        theme: this.theme(character.owner.faction),
        ...tile.ship,
      };
    },
    computeStrikes(actions) {
      const strikes = actions.reduce((acc1, action) => action.strikes.reduce((acc2, strike) => {
        if (strike.action === 'missed') {
          acc2.missed += 1;
        } else if (strike.action === 'hit') {
          acc2.hit += 1;
        } else if (strike.action === 'hit_and_crashed') {
          acc2.hit_and_crashed += 1;
        }
        acc2.damages += strike.damages;
        return acc2;
      }, acc1), { missed: 0, hit: 0, hit_and_crashed: 0, damages: 0 });

      return this.$tmd('panel.operations.fight_strike', {
        damages: Math.round(strikes.damages),
        hit_count: strikes.hit + strikes.hit_and_crashed,
        missed_count: strikes.missed,
        crashed_count: strikes.hit_and_crashed,
      });
    },
    // Compact strike-list label: "2×6", "1×23", or "—" when empty. Mixed sizes
    // (rare) fall back to a slash-joined list.
    strikes(list) {
      if (!Array.isArray(list) || list.length === 0) return '—';
      const allEqual = list.every((d) => d === list[0]);
      return allEqual ? `${list.length}×${list[0]}` : list.join('/');
    },
    theme(faction) {
      return this.themeOf(faction);
    },
    themeOf(factionKey) {
      const factions = this.$store.state.portal.data.faction || [];
      const f = factions.find((x) => x.key === factionKey);
      return f ? f.theme : 'dark-blue';
    },
  },
  components: {
    DefaultLayout,
    SimulatorArmy,
    SimulatorShipPicker,
    SimulatorRoundLog,
    SimulatorDebugView,
    ShipCard,
  },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/shared/variables';

// Tighter side margins than the default 20px bloc: the six army columns
// need every horizontal pixel the 342px aside can give (see the width
// budget comment in SimulatorArmy.vue).
.simulator-army-bloc {
  display: flex;
  justify-content: center;
  margin-left: 10px;
  margin-right: 10px;
}

// In-game army header chips (army.scss) sized for the rail: no extra
// bottom gap above the grid.
.simulator-fleet-stats {
  margin-top: 8px;
  margin-bottom: 0;

  .def-list-prop {
    margin-bottom: 2px;
  }

  .svg-icon {
    width: 14px;
    height: 14px;
    vertical-align: -2px;
  }
}

.simulator-fleet-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 8px;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.simulator-balance {
  display: flex;
  align-items: center;
  gap: 8px;
}

.simulator-launch {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

// 100-run headline: who wins how often, as literal battle counts. Segment
// widths are flex-grow driven so zero-count segments simply vanish.
.simulator-runs-summary {
  margin-bottom: 24px;

  .runs-counts {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 0.95rem;
    margin-bottom: 6px;

    .runs-draws {
      opacity: 0.6;
    }
  }

  .runs-bar {
    display: flex;
    height: 14px;
    border-radius: 3px;
    overflow: hidden;

    > div {
      flex-basis: 0;
    }
  }

  .runs-sub {
    margin-top: 4px;
    font-size: 0.85rem;
    opacity: 0.6;
  }

  .runs-losses {
    margin-top: 10px;
    font-size: 0.9rem;
    opacity: 0.85;

    div {
      margin-bottom: 2px;
    }
  }
}

.simulator-sample-note {
  margin: 0 0 12px;
  font-size: 0.9rem;
  font-style: italic;
  opacity: 0.6;
}

.simulator-empty-hint {
  padding: 40px 20px;
  text-align: center;
  opacity: 0.6;
}

.simulator-results {
  padding: 20px;
}

.simulator-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: solid 1px rgba(255, 255, 255, 0.1);
}

.simulator-tab {
  background: none;
  border: none;
  border-bottom: solid 2px transparent;
  padding: 8px 16px;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
  font-size: 1rem;

  &.is-active {
    opacity: 1;
    border-bottom-color: currentColor;
    font-weight: bold;
  }
}

.simulator-results-side {
  margin-bottom: 24px;

  h2 {
    margin: 0 0 8px 0;
  }
}

.simulator-results-army {
  margin-bottom: 12px;
}

.simulator-hotkeys {
  font-size: 0.95rem;
  opacity: 0.95;

  ul {
    margin: 0;
    padding-left: 0;
    list-style: none;
  }

  li {
    margin-bottom: 8px;
    line-height: 1.5;
  }

  kbd {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 3px;
    border: solid 1px rgba(255, 255, 255, 0.35);
    background: rgba(255, 255, 255, 0.1);
    font-size: 0.9rem;
  }
}

.simulator-info-card {
  .simulator-ship-card {
    width: 300px;
    margin: 0 auto;
    // .card-container is a fixed 418px tall and its cost row hangs below
    // that box: reserve the row's height so nothing underneath overlaps it
    padding-bottom: 38px;
  }

  h3 {
    margin: 16px 0 6px 0;
  }

  .info-stats {
    width: 100%;
    border-collapse: collapse;

    td {
      padding: 3px 0;
      border-bottom: solid 1px rgba(255, 255, 255, 0.06);
    }

    td:first-child {
      opacity: 0.6;
    }

    td:last-child {
      text-align: right;
      font-weight: bold;
    }

    .base-val {
      opacity: 0.45;
      text-decoration: line-through;
      font-weight: normal;
    }

    .arrow {
      opacity: 0.45;
      margin: 0 4px;
    }

    .tuned-val {
      font-weight: bold;
    }
  }

  .info-hint {
    opacity: 0.5;
  }
}

/* --- phone: the ship picker as a bottom sheet --- */

.sim-sheet-root {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 400;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  pointer-events: none;
}

// Only the sheet itself takes taps; the upper half stays live so the
// player can still see (and scroll to) the slot being filled.
.sim-sheet-backdrop {
  flex: 1 1 auto;
  pointer-events: auto;
  background: rgba(0, 0, 0, .35);
}

.sim-sheet {
  pointer-events: auto;
  max-height: 58vh;
  display: flex;
  flex-direction: column;

  background: $grey-darker;
  border-top: solid 1px rgba(255, 255, 255, .2);
  box-shadow: 0 -8px 20px rgba(0, 0, 0, .55);
}

.sim-sheet-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: solid 1px rgba(255, 255, 255, .12);

  .sim-sheet-side {
    font-weight: bold;
    text-transform: uppercase;
    font-size: 1.3rem;
  }

  // The slot being filled, since the sheet covers the grid.
  .sim-sheet-slot {
    padding: 1px 8px;
    border-radius: 3px;
    background: rgba(255, 255, 255, .1);
    font-size: 1.2rem;
    font-variant-numeric: tabular-nums;
  }

  .sim-sheet-close {
    margin-left: auto;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    background: none;
    border: none;
    color: $white;

    .svg-icon {
      width: 13px;
      height: 13px;
    }
  }
}

@each $class, $color in $themes-list {
  .sim-sheet-side.f-#{$class} { color: $color; }
}

.sim-sheet-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

@media screen and (max-width: $mobile-breakpoint) {
  // The launch controls are the page's primary action; give them a full
  // row rather than a sliver beside the balance select.
  .simulator-balance {
    flex: 1 1 100%;
    margin-bottom: 0;
  }

  .simulator-launch {
    display: flex;
    flex: 1 1 100%;
    gap: 6px;

    .default-button { flex: 1 1 0; }
  }

  // Both fleets and the results share one column, so the ship card can
  // use the full width instead of the 342px aside budget.
  .simulator-ship-card {
    zoom: 1 !important;
  }
}
</style>
